/*
 * Knuska mobile navigation.
 *
 * The theme's own MainMenu opens, closes, drills down, traps focus and keeps
 * aria-expanded honest. None of that is repeated here. This adds the three
 * things a drawer needs that a dropdown did not:
 *
 *   - the scrim and the close control close it,
 *   - Escape closes it,
 *   - the page behind it stops scrolling while it is open.
 *
 * Closing goes through the theme's own toggle button rather than removing
 * classes directly, so the theme runs its close path - focus returns to the
 * burger, the drill-down resets, aria-expanded flips - and there is one
 * definition of what "closed" means rather than two that can disagree.
 */
(() => {
  const MOBILE = '(max-width: 768.98px)';

  const menu = document.querySelector('main-menu');
  if (!menu) return;

  const content = menu.querySelector('.main-menu__content');
  const toggle = menu.querySelector('.main-menu__toggle');
  if (!content || !toggle) return;

  const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';
  const onPhone = () => window.matchMedia(MOBILE).matches;

  const close = () => {
    if (isOpen()) toggle.click();
  };

  const scrim = menu.querySelector('[data-kmn-scrim]');
  if (scrim) scrim.addEventListener('click', close);

  menu.querySelectorAll('[data-kmn-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && onPhone() && isOpen()) close();
  });

  /* One open sub-category at a time --------------------------------------- */

  /*
   * The theme opens each sub-category independently, so on a phone several left
   * open push the rest of the list off the screen and the panel stops being a
   * menu you can see the shape of.
   *
   * Siblings are closed by clicking their own toggles rather than by stripping
   * classes: the theme's handler then runs its real close path - the height
   * animation, aria-expanded, the focus bookkeeping - so there is one definition
   * of "closed" rather than a second one here that can drift from it.
   *
   * The capture phase runs this before the theme's own delegated listener, so
   * the siblings are on their way out before the clicked one opens. The event is
   * deliberately not stopped: the theme still handles the click that started it.
   */
  document.addEventListener('click', (event) => {
    if (!onPhone()) return;

    const hit = event.target.closest('.js-toggle');
    const item = hit && hit.closest('.child-nav__item--toggle');
    if (!item) return;

    /* Only when this one is about to open - a close needs no help. */
    if (item.classList.contains('is-open')) return;

    const list = item.parentElement;
    if (!list) return;

    list.querySelectorAll(':scope > .child-nav__item--toggle.is-open').forEach((sibling) => {
      if (sibling === item) return;
      /*
       * This synthetic click re-enters this handler for the sibling, which is
       * open - and an open item returns above. That is the recursion guard, and
       * it is the condition the feature is built on rather than a flag kept in
       * step by hand.
       */
      sibling.querySelector('.js-toggle')?.click();
    });
  }, true);

  /*
   * The lock follows the drawer's own class rather than the toggle's click, so
   * it is still right when the theme closes the menu for its own reasons - a
   * breakpoint change, the search opening, a link being followed.
   */
  const sync = () => {
    const open = content.classList.contains('is-open') && onPhone();
    document.documentElement.classList.toggle('kmn-locked', open);
  };

  new MutationObserver(sync).observe(content, {
    attributes: true,
    attributeFilter: ['class'],
  });

  /*
   * Crossing to desktop leaves no drawer to lock the page behind, and the class
   * observer will not fire if the theme changed nothing about the element.
   */
  window.addEventListener('on:breakpoint-change', sync);

  sync();
})();
